/**
 * SWAYM Cinematic Hero Controller (hero.js)
 * Implements high-performance canvas image preloading and GSAP ScrollTrigger timeline sequence.
 */

(function () {
  document.addEventListener('DOMContentLoaded', () => {
    const heroSection = document.querySelector('.swaym-hero-wrapper');
    if (!heroSection) return;

    const sectionId = heroSection.getAttribute('data-section-id');
    const totalFrames = parseInt(heroSection.getAttribute('data-total-frames')) || 120;
    const framesPattern = heroSection.getAttribute('data-frames-pattern');
    const canvas = document.getElementById(`SwaymHeroCanvas-${sectionId}`);

    if (!canvas || !framesPattern) return;

    const context = canvas.getContext('2d');
    const images = [];
    const airSequence = { frame: 0 };

    // Pad index with leading zeros if necessary (e.g., gate_001.jpg)
    function getFrameUrl(index) {
      const paddedIndex = String(index).padStart(3, '0');
      // If pattern contains ###, replace with padded index, else use index
      if (framesPattern.includes('102')) {
        return framesPattern.replace('102', paddedIndex);
      }
      return framesPattern + index;
    }

    // Preload all frames into memory
    let loadedCount = 0;
    function preloadFrames(callback) {
      for (let i = 1; i <= totalFrames; i++) {
        const img = new Image();
        img.src = getFrameUrl(i);
        img.onload = () => {
          loadedCount++;
          if (loadedCount === totalFrames) {
            callback();
          }
        };
        images.push(img);
      }
    }

    // Scale and center draw image on canvas (cover mode)
    function renderFrame(index) {
      const img = images[index];
      if (!img) return;

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const imgWidth = img.naturalWidth || img.width;
      const imgHeight = img.naturalHeight || img.height;

      const imgRatio = imgWidth / imgHeight;
      const canvasRatio = canvasWidth / canvasHeight;

      let drawWidth = canvasWidth;
      let drawHeight = canvasHeight;
      let offsetX = 0;
      let offsetY = 0;

      if (imgRatio > canvasRatio) {
        drawWidth = canvasHeight * imgRatio;
        offsetX = (canvasWidth - drawWidth) / 2;
      } else {
        drawHeight = canvasWidth / imgRatio;
        offsetY = (canvasHeight - drawHeight) / 2;
      }

      context.clearRect(0, 0, canvasWidth, canvasHeight);
      context.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
    }

    // Resize canvas based on viewport dimensions
    function resizeCanvas() {
      const devicePixelRatio = window.devicePixelRatio || 1;
      canvas.width = canvas.clientWidth * devicePixelRatio;
      canvas.height = canvas.clientHeight * devicePixelRatio;
      renderFrame(airSequence.frame);
    }

    // Initialize ScrollTrigger-controlled animation
    function initScrollAnimation() {
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        console.warn('SwaymMotion: GSAP or ScrollTrigger is missing. Scroll animation bypassed.');
        return;
      }

      // Initial layout setup
      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      // Mobile browsers toggle their address bar during scroll, firing spurious
      // resize events that shift innerHeight mid-scroll.
      ScrollTrigger.config({ ignoreMobileResize: true });

      // Create primary ScrollTrigger timeline
      gsap.to(airSequence, {
        frame: totalFrames - 1,
        snap: 'frame',
        ease: 'none',
        scrollTrigger: {
          trigger: `#SwaymHero-${sectionId}`,
          pin: true,
          scrub: 0.4,
          start: 'top top',
          end: () => `+=${window.innerHeight * 2.5}`, // Controls scroll length duration
          onUpdate: (self) => {
            const progress = self.progress;

            // Render current frame matching progression
            renderFrame(Math.floor(airSequence.frame));

            // State 1 to State 2 transition
            if (progress > 0.04) {
              gsap.to(`.swaym-hero-video-layer`, { opacity: 0, duration: 0.25, overwrite: 'auto' });
              gsap.to(`.swaym-hero-canvas-layer`, { opacity: 1, duration: 0.25, overwrite: 'auto' });
              // Fade out Sanskrit/English headings
              gsap.to(`.swaym-hero-content-layer`, { opacity: 1 - (progress * 6), y: -progress * 60, duration: 0.2, overwrite: 'auto' });
            } else {
              gsap.to(`.swaym-hero-video-layer`, { opacity: 1, duration: 0.25, overwrite: 'auto' });
              gsap.to(`.swaym-hero-canvas-layer`, { opacity: 0, duration: 0.25, overwrite: 'auto' });
              gsap.to(`.swaym-hero-content-layer`, { opacity: 1, y: 0, duration: 0.2, overwrite: 'auto' });
            }

            // Near end of sequence: Reveal Brand Statement & primary CTA button
            if (progress > 0.70) {
              const statementProgress = (progress - 0.70) / 0.30; // 0 to 1 multiplier
              gsap.to(`.swaym-hero-brand-layer`, {
                opacity: statementProgress,
                y: (1 - statementProgress) * -20,
                duration: 0.2,
                overwrite: 'auto'
              });
              gsap.to(`.swaym-hero-cta-layer`, {
                opacity: statementProgress,
                y: (1 - statementProgress) * -10,
                duration: 0.2,
                overwrite: 'auto'
              });
              document.querySelector(`.swaym-hero-cta-layer`).classList.add('visible');
            } else {
              gsap.to(`.swaym-hero-brand-layer`, { opacity: 0, y: 20, duration: 0.2, overwrite: 'auto' });
              gsap.to(`.swaym-hero-cta-layer`, { opacity: 0, y: 10, duration: 0.2, overwrite: 'auto' });
              document.querySelector(`.swaym-hero-cta-layer`).classList.remove('visible');
            }
          }
        }
      });
    }

    // Begin preloading sequence
    preloadFrames(() => {
      initScrollAnimation();
      // Render first frame as initial canvas state
      renderFrame(0);

      // Content loading below the fold after the pin is created can change the
      // document's total height, leaving the pin-spacer sized incorrectly and
      // the pin unable to release. Recalculating once everything has settled
      // keeps the pin's scroll distance in sync with the real page layout.
      window.addEventListener('load', () => {
        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.refresh();
        }
      });
    });
  });
})();
